// Copy of the "Settings" walkthrough. The rules every text follows are in ../../de/help.js.
// Field and tab names read as the page shows them in English (../settings.js, ../notification.js,
// ../billing.js, ../dataexport.js).
export default {
  title: "Settings",
  // Short names of the views, for the header, the timeline and "Continue to …".
  section: {
    SettingsOverview: "Settings",
    EmployeeDetail: "Employee",
    ProjectCostsForOne: "Project costs",
    TimeTrackingMy: "My hours",
    PersonnelCalendar: "Personnel planning",
  },
  // The pages outside the settings where one of these defaults is overridden or visibly at work.
  related: "Connections",
  step: {
    head: {
      text: "This page is about the whole workspace, not about your account. The line below names the versions of the interface and the server.",
    },
    save: {
      text: "Save stores the fields from General, the terminology, the default role, edited roles and the notification thresholds. Everything else here takes effect at once.",
    },

    "tab-general": {
      text: "General holds what applies everywhere. Logo, currency, first day of the week, employer's contribution and the company address.",
    },
    logo: {
      text: "The logo sits top left in the menu next to the company name. It is uploaded at once, as a PNG or JPEG of up to 2 MB.",
    },
    currency: {
      text: "The default currency and the currency format decide how amounts are shown. The format sets the separators and where the currency sign goes.",
    },
    weekstart: {
      text: "The first day of the week applies to every date picker, to calendars, to My hours and to the weekly cost table of a project.",
    },
    contribution: {
      text: "The employer's contribution is added to labour cost in the running calculation. An employee may carry their own rate, which then wins.",
    },
    daysubmission: {
      text: "With the switch on, the app sends every day submission with its location. Without permission it does not record, and if the phone finds none, it adds it up to 15 minutes later.",
    },
    timeentry: {
      text: "With the switch on, the app clocks in and adds a crew day only once the location is allowed and turned on. Clocking out always works.",
    },
    address: {
      text: "This address is the sender on every PDF, on invoices and [[cr.pl]] among them. The company name cannot be changed here.",
    },

    "tab-terminology": {
      text: "Terminology sets what your company calls its records. A daywork sheet or a T&M ticket, say, instead of an hourly wage report.",
    },
    "terminology-names": {
      text: "One name per record, picked from the list or typed. It then appears throughout Lewara and on newly printed PDFs. Each language has names of its own; the language menu above the table picks which ones it shows.",
    },
    "terminology-preview": {
      text: "The sentence shows the choice in context. If article and plural are right here, they are right everywhere.",
    },

    "tab-rights": {
      text: "Permissions decide who may see and change what. A role applies to everybody who holds it.",
    },
    "default-role": {
      text: "The default role is already filled in for a new login user. It can still be changed in the form.",
    },
    "role-list": {
      text: "Every role of the company is listed here. Which role a person holds is on their own page, on the System specific tab.",
    },
    "role-rights": {
      text: "A click on a role opens its permissions. Administrator always holds all of them and cannot be changed.",
    },
    "role-tree": {
      text: "A click on a group opens its single permissions. A permission that requires another one sets that one too and locks it. An empty cell means that permission does not exist for the item; a price, for instance, has no delete of its own.",
    },
    "role-close": {
      text: "The cross discards the changes to this role. They are only stored once you press Save below.",
    },

    "tab-absence": {
      text: "Absence types are the reasons somebody is away. They are assigned in the personnel planning or in Review. Time tracking credits paid types to the time account.",
    },
    "absence-default": {
      text: "Every workspace comes with these six types. Their colours draw the bars in the personnel planning and the days off in the time tracking. The Paid switch applies to past days as well.",
    },
    "absence-custom": {
      text: "Your own types are added with a name and a colour. The plus saves one at once, so Save below is not needed for it.",
    },

    "tab-notification": {
      text: "Notifications decide when the bell above reports something. Every monitor is a row with a checkbox of its own.",
    },
    "notify-levels": {
      text: "The Warning and Critical columns decide from which value a notification appears at which level. The number on the bell is coloured after the most urgent unread one.",
    },
    "notify-invoicing": {
      text: "After this many days without an invoice or without [[cr:a/an]] [[cr]], a project reports itself. The click opens the invoice wizard or the report tab.",
    },
    "notify-costs": {
      text: "Margin, budget, a falling margin and progress behind plan report a project running out of hand. The click leads to the running calculation or the service catalogue.",
    },
    "notify-time": {
      text: "Missing days, pending approvals and a clock still running report themselves here. The click leads to the review screen or the attendance board.",
    },
    "notify-documents": {
      text: "A document reports itself before its due date, and the click opens the folder filtered. The e-mail switch at the top also sends notifications by e-mail.",
    },

    worktimemodel: {
      text: "Working-time models belong to the time tracking. Its own walkthrough shows how target hours, rounding and breaks come out of them.",
    },

    "pdf-templates": {
      text: "For each document, this shows what it prints with. “Customize” sets what it shows and which letterhead it prints on. A project can pick another template for itself.",
    },

    "pdf-letterheads": {
      text: "A letterhead sets how the pages look: logo, footer, font and colours. “Use for documents” moves the documents you pick onto it.",
    },

    "tab-billing": {
      text: "Subscription shows the plan of this workspace. Add-ons such as the time tracking unlock their areas here.",
    },
    "billing-plan": {
      text: "Plan, used seats and the add-ons you bought are here. A workspace that is exempt from billing only sees a notice instead.",
    },
    "billing-manage": {
      text: "Change plan and payment method lead to pages of their own. Cancelling takes effect at the end of the term and needs the right for it.",
    },

    "tab-dataexport": {
      text: "Data export hands you every record of the workspace. Only the Administrator role sees this tab.",
    },
    "export-request": {
      text: "Request export queues a ZIP archive. It is built in the background, so you can leave the page.",
    },
    "export-list": {
      text: "The list shows every request with its status, contents and size. A finished archive is deleted once its deadline has passed.",
    },

    again: { text: "This button is on every page that has an explanation. One click starts it again." },

    "employee-tabs": {
      text: "An employee may carry values of their own. System specific holds what they may do, Company relevant what the company calculates with them.",
    },
    "employee-roles": {
      text: "The role is assigned here. The default role from the settings is only the suggestion when the person is created.",
    },
    "employee-contribution": {
      text: "An employer's contribution entered here is used instead of the rate from the settings. Empty means the general rate counts.",
    },

    "costs-contribution": {
      text: "Where a rate is set, the employer's contribution sits in every project's cost to date. Its labour cost is then a full cost.",
    },
    "times-weekstart": {
      text: "The first day of the week decides which day this week starts on. Actual, target and balance beside it count exactly those seven days.",
    },
    "planning-absence": {
      text: "Absence types are assigned here. A bar takes the colour of its type, which wins over the colour of the project.",
    },
  },
  // What the pointer types. A sample name, never saved.
  typed: {
    absence: "Training",
  },
}
